import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import { useToast } from "../context/ToastContext";

type EconomicParameters = {
  id: string | number;
  valid_from: string;
  valid_to: string | null;
  vat_treatment_pct: number | string;
  iibb_pct: number | string;
  card_1_fee_pct: number | string;
  card_1_fee_vat_pct: number | string;
  card_6_fee_pct: number | string;
  card_6_fee_vat_pct: number | string;
  integration_fund_pct: number | string;
  max_seller_pct: number | string;
  max_total_incentives_pct: number | string;
  min_mauvelore_margin_pct: number | string;
  target_mauvelore_margin_pct: number | string;
  created_at?: string;
};

function pct(value: number | string) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function toDecimal(value: string) {
  const n = Number(value);
  return Number.isFinite(n) ? n / 100 : NaN;
}

export default function EconomicParametersConfig() {
  const { show } = useToast();

  const [items, setItems] = useState<EconomicParameters[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [validFrom, setValidFrom] = useState(today());
  const [vat, setVat] = useState("21");
  const [iibb, setIibb] = useState("3.5");
  const [card1Fee, setCard1Fee] = useState("6");
  const [card1Vat, setCard1Vat] = useState("21");
  const [card6Fee, setCard6Fee] = useState("19");
  const [card6Vat, setCard6Vat] = useState("21");
  const [integrationFund, setIntegrationFund] = useState("4");
  const [maxSeller, setMaxSeller] = useState("19");
  const [maxTotalIncentives, setMaxTotalIncentives] = useState("22");
  const [minMargin, setMinMargin] = useState("15");
  const [targetMargin, setTargetMargin] = useState("20");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.getEconomicParameters());
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar los parámetros económicos.", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    const current = items.find((x) => x.valid_to == null);

    setValidFrom(today());

    if (current) {
      setVat(String(Number(current.vat_treatment_pct) * 100));
      setIibb(String(Number(current.iibb_pct) * 100));
      setCard1Fee(String(Number(current.card_1_fee_pct) * 100));
      setCard1Vat(String(Number(current.card_1_fee_vat_pct) * 100));
      setCard6Fee(String(Number(current.card_6_fee_pct) * 100));
      setCard6Vat(String(Number(current.card_6_fee_vat_pct) * 100));
      setIntegrationFund(String(Number(current.integration_fund_pct) * 100));
      setMaxSeller(String(Number(current.max_seller_pct) * 100));
      setMaxTotalIncentives(String(Number(current.max_total_incentives_pct) * 100));
      setMinMargin(String(Number(current.min_mauvelore_margin_pct) * 100));
      setTargetMargin(String(Number(current.target_mauvelore_margin_pct) * 100));
    }

    setShowForm(true);
  }

  function closeForm() {
    if (!submitting) setShowForm(false);
  }

  async function submit(e: any) {
    e.preventDefault();

    const values = {
      vat_treatment_pct: toDecimal(vat),
      iibb_pct: toDecimal(iibb),
      card_1_fee_pct: toDecimal(card1Fee),
      card_1_fee_vat_pct: toDecimal(card1Vat),
      card_6_fee_pct: toDecimal(card6Fee),
      card_6_fee_vat_pct: toDecimal(card6Vat),
      integration_fund_pct: toDecimal(integrationFund),
      max_seller_pct: toDecimal(maxSeller),
      max_total_incentives_pct: toDecimal(maxTotalIncentives),
      min_mauvelore_margin_pct: toDecimal(minMargin),
      target_mauvelore_margin_pct: toDecimal(targetMargin),
    };

    if (!validFrom) {
      show("Ingresá una fecha de vigencia.", "error");
      return;
    }

    if (Object.values(values).some((v) => !Number.isFinite(v) || v < 0 || v > 1)) {
      show("Todos los porcentajes deben estar entre 0% y 100%.", "error");
      return;
    }

    if (values.target_mauvelore_margin_pct < values.min_mauvelore_margin_pct) {
      show("El margen objetivo no puede ser menor que el margen mínimo.", "error");
      return;
    }

    if (values.max_total_incentives_pct < values.max_seller_pct) {
      show("El máximo total de incentivos no puede ser menor que el máximo del vendedor.", "error");
      return;
    }

    setSubmitting(true);

    try {
      await api.createEconomicParameters({
        valid_from: validFrom,
        ...values,
      });

      show("Nueva versión de parámetros económicos creada.", "success");
      setShowForm(false);
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron guardar los parámetros económicos.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="kanban-header">
        <div>
          <h2>Parámetros económicos</h2>
          <p className="meta">
            Reglas financieras generales versionadas por vigencia.
          </p>
        </div>

        <button className="btn-primary" onClick={openNew}>
          + Nueva versión
        </button>
      </div>

      {loading && <div className="loading-strip">Cargando...</div>}

      <div className="contact-list">
        {items.map((item) => (
          <div key={item.id} className="contact-row">
            <div style={{ flex: 1 }}>
              <div className="name">
                Vigencia desde {String(item.valid_from).slice(0, 10)}
                {item.valid_to == null ? " · Vigente" : " · Histórica"}
              </div>

              <div className="meta">
                IVA: {pct(item.vat_treatment_pct)}
                {" · IIBB: "}
                {pct(item.iibb_pct)}
                {" · Margen mínimo: "}
                {pct(item.min_mauvelore_margin_pct)}
                {" · Margen objetivo: "}
                {pct(item.target_mauvelore_margin_pct)}
              </div>

              <div className="meta">
                Tarjeta 1 pago: {pct(item.card_1_fee_pct)}
                {" + IVA "}
                {pct(item.card_1_fee_vat_pct)}
                {" · Hasta 6 cuotas: "}
                {pct(item.card_6_fee_pct)}
                {" + IVA "}
                {pct(item.card_6_fee_vat_pct)}
              </div>

              <div className="meta">
                Fondo integración: {pct(item.integration_fund_pct)}
                {" · Máx. vendedor: "}
                {pct(item.max_seller_pct)}
                {" · Máx. incentivos: "}
                {pct(item.max_total_incentives_pct)}
              </div>

              {item.valid_to && (
                <div className="meta">
                  Vigente hasta {String(item.valid_to).slice(0, 10)}
                </div>
              )}
            </div>
          </div>
        ))}

        {!loading && items.length === 0 && (
          <p className="meta">
            No hay parámetros económicos configurados. Creá la primera versión.
          </p>
        )}
      </div>

      {showForm && (
        <div className="modal-backdrop" onClick={closeForm}>
          <div
            className="modal modal-wide"
            style={{
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>Nueva versión de parámetros económicos</h3>

            <p className="meta">
              Los valores históricos no se modifican. Al crear una nueva versión,
              la anterior se cierra automáticamente.
            </p>

            <form onSubmit={submit} className="form-grid">
              <label>Vigente desde</label>
              <input
                type="date"
                value={validFrom}
                onChange={(e: any) => setValidFrom(e.target.value)}
                required
              />

              <label>IVA tratamiento (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={vat} onChange={(e: any) => setVat(e.target.value)} />

              <label>IIBB (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={iibb} onChange={(e: any) => setIibb(e.target.value)} />

              <label>Tarjeta 1 pago · comisión estándar (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={card1Fee} onChange={(e: any) => setCard1Fee(e.target.value)} />

              <label>Tarjeta 1 pago · IVA comisión (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={card1Vat} onChange={(e: any) => setCard1Vat(e.target.value)} />

              <label>Hasta 6 cuotas · comisión estándar (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={card6Fee} onChange={(e: any) => setCard6Fee(e.target.value)} />

              <label>Hasta 6 cuotas · IVA comisión (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={card6Vat} onChange={(e: any) => setCard6Vat(e.target.value)} />

              <label>Fondo de integración (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={integrationFund} onChange={(e: any) => setIntegrationFund(e.target.value)} />

              <label>Máximo vendedor (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={maxSeller} onChange={(e: any) => setMaxSeller(e.target.value)} />

              <label>Máximo total incentivos (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={maxTotalIncentives} onChange={(e: any) => setMaxTotalIncentives(e.target.value)} />

              <label>Margen mínimo Mauvelore (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={minMargin} onChange={(e: any) => setMinMargin(e.target.value)} />

              <label>Margen objetivo Mauvelore (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={targetMargin} onChange={(e: any) => setTargetMargin(e.target.value)} />

              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={closeForm} disabled={submitting}>
                  Cancelar
                </button>

                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Guardando..." : "Crear versión"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
